import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Lock, Loader2, Check, Clock, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { resolveMediaUrl } from "@/lib/mediaUrl";
import { useApp } from "@/context/AppContext";

// Lot 749 — feuille « Partagé dans N groupes » : groupes publics (et privés dont je suis membre) où la publication a
// été partagée → Rejoindre (public : tout de suite ; privé : demande) / Ouvrir. Les privés où je ne suis pas sont comptés.
// Lot 750 — « Retirer » (corbeille) sur les groupes où J'AI partagé (`my_share_id`) : confirmation EN LIGNE dans la
// rangée (la feuille Radix bloque les clics hors de son contenu et est translatée : pas de popup superposée) puis
// `DELETE /posts/{id}/shared-groups/{gid}` ; la ligne disparaît si plus personne n'y a partagé (`onRemoved(gid, r)`).
export const SharedGroupsSheet = ({ postId, open, onOpenChange, onRemoved }) => {
  const { t } = useApp();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null);
  useEffect(() => {
    if (!open) return;
    setData(null);
    api.get(`/posts/${postId}/shared-groups`).then(({ data: d }) => setData(d)).catch(() => setData({ groups: [], total: 0, hidden_private: 0 }));
  }, [open, postId]);
  const join = async (g) => {
    setBusy(g.id);
    try {
      const { data: r } = await api.post(`/groups/${g.id}/join`);
      setData((d) => ({ ...d, groups: d.groups.map((x) => (x.id === g.id ? { ...x, is_member: !!r.member, pending: !!r.pending, member_count: x.member_count + (r.member ? 1 : 0) } : x)) }));
      toast.success(r.member ? t("post.gs_joined", "Tu as rejoint le groupe") : t("post.gs_request_sent", "Demande envoyée"));
    } catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || t("common.error", "Erreur")); }
    finally { setBusy(null); }
  };
  const unshare = async (g) => {
    setConfirm(null);
    setBusy(g.id);
    try {
      const { data: r } = await api.delete(`/posts/${postId}/shared-groups/${g.id}`);
      setData((d) => ({
        ...d,
        total: r.still_shared ? d.total : Math.max(0, d.total - 1),
        groups: r.still_shared ? d.groups.map((x) => (x.id === g.id ? { ...x, my_share_id: null } : x)) : d.groups.filter((x) => x.id !== g.id),
      }));
      onRemoved?.(g.id, r);
      toast.success(t("post.gs_unshared", "Partage retiré"));
    } catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || t("common.error", "Erreur")); }
    finally { setBusy(null); }
  };
  const tid = `shared-groups-${postId}`;
  const n = data?.total ?? 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="bottom" className="z-[130]" overlayClassName="z-[130] bg-black/60" style={{ "--yalola-bottom-sheet-h": "70dvh" }} data-testid={tid}>
        <DialogHeader>
          <DialogTitle>{data ? (n === 1 ? t("post.gs_count_one", "Partagé dans 1 groupe") : t("post.gs_count", "Partagé dans {n} groupes").replace("{n}", String(n))) : t("post.gs_sheet_title", "Groupes")}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-1.5">
          {!data && <div className="py-6 grid place-items-center" data-testid={`${tid}-loading`}><Loader2 size={20} className="animate-spin" /></div>}
          {data?.groups.map((g) => (
            <div key={g.id} className="flex items-center gap-3 rounded-2xl border p-2.5" style={{ borderColor: "var(--yalola-border)" }} data-testid={`${tid}-row-${g.id}`}>
              {confirm !== g.id && (
                <>
                  <button type="button" onClick={() => { onOpenChange(false); nav(`/groups/${g.id}`); }} className="h-10 w-10 rounded-xl overflow-hidden shrink-0 grid place-items-center" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`${tid}-open-${g.id}`}>
                    {g.cover_url ? <img src={resolveMediaUrl(g.cover_url)} alt="" className="h-full w-full object-cover" /> : <Users size={18} style={{ color: "var(--yalola-text-muted)" }} />}
                  </button>
                  <button type="button" onClick={() => { onOpenChange(false); nav(`/groups/${g.id}`); }} className="flex-1 min-w-0 text-left">
                    <span className="block text-sm font-semibold truncate">{g.name}</span>
                    <span className="block text-xs flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }}>
                      {g.privacy === "private" && <Lock size={11} />}{t("group.members_n", "{n} membre(s)").replace("{n}", String(g.member_count || 0))}
                    </span>
                  </button>
                </>
              )}
              {confirm === g.id ? (
                <div className="flex-1 min-w-0 flex items-center gap-2 min-h-10" data-testid={`${tid}-unshare-ask-${g.id}`}>
                  <Trash2 size={15} className="shrink-0" style={{ color: "#e11d48" }} />
                  <span className="flex-1 min-w-0 text-xs"><b>{t("post.gs_unshare_title", "Retirer ce partage ?")}</b><span className="block truncate" style={{ color: "var(--yalola-text-muted)" }}>{g.name}</span></span>
                  <button type="button" onClick={() => unshare(g)} className="h-7 px-3 rounded-full text-xs font-semibold text-white shrink-0" style={{ background: "#e11d48" }} data-testid={`${tid}-unshare-confirm-${g.id}`}>{t("common.yes", "Oui")}</button>
                  <button type="button" onClick={() => setConfirm(null)} className="h-7 px-3 rounded-full text-xs font-semibold shrink-0" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`${tid}-unshare-cancel-${g.id}`}>{t("common.no", "Non")}</button>
                </div>
              ) : (
                <>
                  {g.is_member ? (
                    <span className="text-xs font-semibold inline-flex items-center gap-1 shrink-0" style={{ color: "var(--yalola-primary)" }} data-testid={`${tid}-member-${g.id}`}><Check size={13} /> {t("post.gs_member", "Membre")}</span>
                  ) : g.pending ? (
                    <span className="text-xs font-semibold inline-flex items-center gap-1 shrink-0" style={{ color: "var(--yalola-text-muted)" }} data-testid={`${tid}-pending-${g.id}`}><Clock size={13} /> {t("post.gs_pending", "En attente")}</span>
                  ) : (
                    <Button size="sm" className="rounded-full shrink-0" onClick={() => join(g)} disabled={busy === g.id} data-testid={`${tid}-join-${g.id}`}>
                      {busy === g.id ? <Loader2 size={14} className="animate-spin" /> : t("groups.join", "Rejoindre")}
                    </Button>
                  )}
                  {g.my_share_id && (
                    <button type="button" onClick={() => setConfirm(g.id)} disabled={busy === g.id} title={t("post.gs_unshare", "Retirer")} aria-label={t("post.gs_unshare", "Retirer")}
                            className="h-8 w-8 rounded-full grid place-items-center shrink-0 transition-colors hover:bg-[color:var(--yalola-surface-alt)]" style={{ color: "#e11d48" }} data-testid={`${tid}-unshare-${g.id}`}>
                      {busy === g.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={15} />}
                    </button>
                  )}
                </>
              )}
            </div>
          ))}
          {data && !data.groups.length && !data.hidden_private && (
            <p className="text-sm text-center py-4" style={{ color: "var(--yalola-text-muted)" }} data-testid={`${tid}-empty`}>{t("post.gs_sheet_empty", "Aucun groupe à afficher.")}</p>
          )}
          {data?.hidden_private > 0 && (
            <p className="text-[11px] text-center pt-2" style={{ color: "var(--yalola-text-muted)" }} data-testid={`${tid}-hidden`}>
              <Lock size={11} className="inline mr-1" />{t("post.gs_sheet_private", "+ {n} groupe(s) privé(s) dont tu n'es pas membre").replace("{n}", String(data.hidden_private))}
            </p>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};
